import React, { useState, useRef } from 'react';
import { ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  onFileUpload: (file: File) => Promise<string>;
  accept?: string;
  maxSize?: number; // en MB
  className?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onFileUpload,
  accept = "image/*,.pdf",
  maxSize = 5,
  className = ""
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedPath, setUploadedPath] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file: File) => {
    // Validar tamaño
    if (file.size > maxSize * 1024 * 1024) {
      alert(`El archivo es demasiado grande. Máximo ${maxSize}MB`);
      return;
    }

    // Validar tipo
    const allowedTypes = accept.split(',').map(type => type.trim());
    const fileType = file.type;
    const fileName = file.name.toLowerCase();
    
    const isValidType = allowedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileName.endsWith(type);
      } else {
        return fileType.match(type.replace('*', '.*'));
      }
    });

    if (!isValidType) {
      alert('Tipo de archivo no permitido');
      return;
    }

    setUploadedFile(file);
    onFileSelect(file);

    // Subir archivo automáticamente
    setIsUploading(true);
    try {
      const filePath = await onFileUpload(file);
      setUploadedPath(filePath);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error al subir el archivo');
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setUploadedPath('');
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`w-full ${className}`}>
      {!uploadedFile ? (
        <div
          className={`border-2 border-dashed rounded-xl p-5 sm:p-6 text-center cursor-pointer transition-colors bg-white/70 ${
            isDragging
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50">
            <ArrowUpTrayIcon className="h-6 w-6 text-primary-500" />
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-primary-600 hover:text-primary-500">
                Haz clic para subir
              </span>{' '}
              o arrastra y suelta
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PNG, JPG, PDF hasta {maxSize}MB
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileInput}
            className="hidden"
            aria-label="Seleccionar archivo"
          />
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl p-4 bg-white/80">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                <DocumentTextIcon className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {uploadedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(uploadedFile.size)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isUploading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              )}
              {uploadedPath && (
                <span className="text-xs text-green-600 font-medium">
                  ✓ Subido
                </span>
              )}
              <button
                type="button"
                onClick={removeFile}
                className="text-gray-400 hover:text-red-500 text-xs font-medium"
                title="Eliminar archivo"
                aria-label="Eliminar archivo"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
